import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  bool,
  oneOf,
  shape,
  string
} from 'prop-types';
import ReactCursorPosition from 'react-cursor-position';

import { ExperienceContext } from '@thd-nucleus/experience-context';

import { PositiveSpaceLens } from './lens/positive-space';
import { getLensCursorOffset } from './helpers/lens';
import { getEnlargedImageContainerDimension } from './helpers/dimensions';
import {
  getContainerStyle,
  getSmallImageStyle
} from './helpers/styles';
import {
  EnlargedImagePosition,
  EnlargedImageContainerDimensions,
  LargeImageShape,
  SmallImageShape
} from './propTypes';
import {
  ENLARGED_IMAGE_POSITION
} from './constants';
import { EnlargedImage } from './EnlargedImage';

/*
  *DO NOT DELETE THIS SECTION*

  This code was originally from react-image-magnify
  This has been pulled in for modifications and updates after the repo has been stale for 4+ years

  MIT License

  Copyright (c) 2016 Ethan Selzer

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
*/

export const ReactImageMagnify = ({
  isActivatedOnTouch,
  lensStyle,
  smallImage,
  largeImage,
  enlargedImageContainerClassName,
  enlargedImageContainerDimensions,
  enlargedImagePosition: userDefinedEnlargedImagePosition,
  fetchPriority
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const {
    isFluidWidth
  } = smallImage;

  const [smallImageFluidWidth, setSmallImageFluidWidth] = useState(0);
  const [smallImageFluidHeight, setSmallImageFluidHeight] = useState(0);

  const smallImageRef = useRef();

  const setSmallImageDimensionState = () => {
    const {
      offsetWidth: smallImageWidth,
      offsetHeight: smallImageHeight
    } = smallImageRef.current;

    setSmallImageFluidWidth(smallImageWidth);
    setSmallImageFluidHeight(smallImageHeight);
  };

  const onSmallImageLoad = (e) => {
    if (isFluidWidth) {
      setSmallImageDimensionState();
    }
  };

  useEffect(() => {
    if (isFluidWidth) {
      setSmallImageDimensionState();
      window.addEventListener('resize', setSmallImageDimensionState);
    }
    return () => {
      if (isFluidWidth) {
        window.removeEventListener('resize', setSmallImageDimensionState);
      }
    };
  }, []);

  const updatedSmallImage = isFluidWidth
    ? {
      ...smallImage,
      width: smallImageFluidWidth,
      height: smallImageFluidHeight
    }
    : smallImage;

  const isInPlaceMode = userDefinedEnlargedImagePosition
    ? userDefinedEnlargedImagePosition === ENLARGED_IMAGE_POSITION.over
    : isMobile;

  const getUpdatedEnlargedImageContainerDimensions = () => {
    const {
      width: containerWidth,
      height: containerHeight
    } = enlargedImageContainerDimensions;
    const {
      width: smallImageWidth,
      height: smallImageHeight
    } = updatedSmallImage;

    return {
      width: getEnlargedImageContainerDimension({
        containerDimension: containerWidth,
        smallImageDimension: smallImageWidth,
        isInPlaceMode
      }),
      height: getEnlargedImageContainerDimension({
        containerDimension: containerHeight,
        smallImageDimension: smallImageHeight,
        isInPlaceMode
      })
    };
  };

  const shouldShowLens = !isInPlaceMode && !isMobile;

  const cursorOffset = getLensCursorOffset(
    updatedSmallImage,
    largeImage,
    getUpdatedEnlargedImageContainerDimensions()
  );

  return (
    <ReactCursorPosition
      hoverDelayInMs={250}
      hoverOffDelayInMs={150}
      isActivatedOnTouch={isActivatedOnTouch}
      shouldStopTouchMovePropagation
      style={getContainerStyle(updatedSmallImage)}
    >
      {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
      <img
        data-testid="small-image"
        src={updatedSmallImage.src}
        srcSet={updatedSmallImage.srcSet}
        sizes={updatedSmallImage.sizes}
        alt={updatedSmallImage.alt}
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority={fetchPriority}
        style={getSmallImageStyle(updatedSmallImage)}
        ref={smallImageRef}
        onLoad={onSmallImageLoad}
      />
      {shouldShowLens
        && (
          <PositiveSpaceLens
            cursorOffset={cursorOffset}
            smallImage={updatedSmallImage}
            style={lensStyle}
          />
        )}
      <EnlargedImage
        containerClassName={enlargedImageContainerClassName}
        containerDimensions={getUpdatedEnlargedImageContainerDimensions()}
        cursorOffset={cursorOffset}
        largeImage={largeImage}
        smallImage={updatedSmallImage}
        isInPlaceMode={isInPlaceMode}
      />
    </ReactCursorPosition>
  );
};

ReactImageMagnify.propTypes = {
  isActivatedOnTouch: bool,
  lensStyle: shape({
    backgroundImage: string
  }),
  smallImage: SmallImageShape.isRequired,
  largeImage: LargeImageShape.isRequired,
  enlargedImageContainerClassName: string,
  enlargedImageContainerDimensions: EnlargedImageContainerDimensions,
  enlargedImagePosition: EnlargedImagePosition,
  fetchPriority: oneOf([
    'high', // Fetch the image at a high priority relative to other images.
    'low', // Fetch the image at a low priority relative to other images.
    'auto' // Indicates no preference for the fetch priority. The browser decides what is best for the user.
  ])
};

ReactImageMagnify.defaultProps = {
  lensStyle: {
    backgroundImage: ''
  },
  enlargedImageContainerClassName: '',
  enlargedImageContainerDimensions: {
    width: '100%',
    height: '100%'
  },
  fetchPriority: 'auto',
  enlargedImagePosition: 'beside',
  isActivatedOnTouch: false
};